function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useRef, useState, useLayoutEffect, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Downshift from "downshift";
import { useRect } from "@reach/rect";
import { isEmpty, noop, isFunction } from "lodash-es";
import { ExpandMore, Close, Search } from "../../icons";
import { Label } from "../label/Label";
import { useId, onEnter } from "../../helpers";
import { Input } from "semantic-ui-react";
import delay from "lodash/delay";
const TEXT_FIELD_OFFSET_WIDTH = 38;
const Select = _ref => {
  let {
    value,
    options,
    prefixClassName,
    disabled,
    readOnly,
    onChange,
    error,
    style,
    label,
    placeholder,
    maxHeight,
    clearable,
    id,
    className,
    isLoading,
    required,
    emptyCase,
    getRelativeViewportOffset,
    onInputChange,
    onBlur,
    shouldFilter,
    inputPlaceholder
  } = _ref;
  const innerId = useId();
  const labelId = id || `tui-select-${innerId}`;
  const selectRef = useRef();
  const optionsRef = useRef();
  const inputRef = useRef();
  const inputSearchRef = useRef();
  const rect = useRect(selectRef) || {};
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedItem, setSelectedItem] = useState({});
  useEffect(() => {
    if (value !== undefined && value !== null && value !== "") {
      setSelectedItem(options.find(e => e.value === value) || {});
    }
  }, [value, options]);
  useEffect(() => {
    const scrollCallback = e => {
      if (!optionsRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    window.addEventListener("wheel", scrollCallback);
    return () => window.removeEventListener("wheel", scrollCallback);
  }, [selectRef]);
  useLayoutEffect(() => {
    const relativeViewportOffset = getRelativeViewportOffset === null || getRelativeViewportOffset === void 0 ? void 0 : getRelativeViewportOffset();
    const inputMargin = 4;
    let styles = {
      top: `${relativeViewportOffset ? rect.top + rect.height + inputMargin - (relativeViewportOffset.top || 0) : rect.top + rect.height + inputMargin}px`,
      left: `${relativeViewportOffset ? rect.left - (relativeViewportOffset.left || 0) : rect.left}px`,
      right: `${rect.right}px`,
      width: `${rect.width}px`,
      minHeight: `${rect.height}px`,
      transform: null
    };
    const collidesWithBottom = relativeViewportOffset !== null && relativeViewportOffset !== void 0 && relativeViewportOffset.clearanceHeight ? (relativeViewportOffset === null || relativeViewportOffset === void 0 ? void 0 : relativeViewportOffset.clearanceHeight) < maxHeight || window.innerHeight < rect.bottom + maxHeight : window.innerHeight < rect.bottom + maxHeight;
    if (collidesWithBottom) {
      styles.transform = `translateY(-100%) translateY(-${rect.height + inputMargin * 2}px)`;
    }
    Object.entries(styles).forEach(_ref2 => {
      let [key, value] = _ref2;
      optionsRef.current.style[key] = value;
    });
  }, [rect.top, rect.left, rect.right, rect.width, rect.height, rect.bottom, getRelativeViewportOffset]);
  const handleSearchInputChange = event => {
    const value = event.target.value;
    setSearchText(value);
    if (isFunction(onInputChange)) {
      onInputChange(value);
    }
  };
  const onClearInput = () => {
    setSearchText("");
  };
  return /*#__PURE__*/React.createElement("div", {
    className: `${prefixClassName}-wrapper ${className}`,
    style: {
      display: readOnly && isEmpty(selectedItem) ? "none" : "initial"
    }
  }, /*#__PURE__*/React.createElement(Downshift, {
    isOpen: isOpen,
    onChange: selection => {
      inputRef.current.blur();
      if ((selection === null || selection === void 0 ? void 0 : selection.value) !== selectedItem.value) {
        onChange({
          target: selection
        });
      }
    },
    itemToString: item => (item === null || item === void 0 ? void 0 : item.label) || "",
    selectedItem: selectedItem,
    onOuterClick: event => {
      if (!options.find(e => e.value === inputRef.current.value) && selectedItem.value === undefined) {
        inputRef.current.value = "";
      }
      if (onBlur) {
        onBlur(event);
      }
      setIsOpen(false);
      setSearchText("");
    },
    stateReducer: (state, changes) => {
      if (onInputChange || shouldFilter) {
        if ([Downshift.stateChangeTypes.mouseUp, Downshift.stateChangeTypes.blurInput].includes(changes.type)) {
          return {
            ...changes,
            isOpen: state.isOpen
          };
        }
      }
      return changes;
    },
    onStateChange: changes => {
      if (Object.prototype.hasOwnProperty.call(changes, "isOpen")) {
        setIsOpen(changes.isOpen);
      }
    }
  }, _ref3 => {
    let {
      getLabelProps,
      getInputProps,
      getItemProps,
      getMenuProps,
      selectedItem,
      isOpen,
      openMenu,
      closeMenu,
      inputValue
    } = _ref3;
    function onExpand(e) {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !readOnly) {
        if (isOpen) {
          closeMenu();
        } else {
          openMenu();
          if (onInputChange || shouldFilter) {
            delay(() => {
              if (inputSearchRef && inputSearchRef.current) {
                inputSearchRef.current.focus();
              }
            }, 100);
          }
        }
      }
    }
    function onClose(e) {
      e.preventDefault();
      e.stopPropagation();
      onChange({
        target: {
          value: null
        }
      });
    }
    let filterOptions = selectedItem.label === inputValue ? options : options.filter(item => !inputValue || (item === null || item === void 0 ? void 0 : item.label.toLowerCase().includes(inputValue.toLowerCase())));
    if (shouldFilter) {
      filterOptions = filterOptions.filter(_ref4 => {
        let {
          label
        } = _ref4;
        return label === null || label === void 0 ? void 0 : label.toUpperCase().includes(searchText.toUpperCase());
      });
    }
    return /*#__PURE__*/React.createElement("div", {
      // prevents downshift from auto adding prop
      "aria-labelledby": null,
      style: {
        ...style,
        position: "relative"
      }
    }, label ? /*#__PURE__*/React.createElement(Label, _extends({}, getLabelProps({
      id: null
    }), {
      htmlFor: labelId,
      required: required
    }), label) : null, /*#__PURE__*/React.createElement("div", getInputProps({
      id: null,
      onClick: () => {
        if (!disabled && !readOnly && !isLoading) {
          openMenu();
          if (onInputChange || shouldFilter) {
            delay(() => {
              if (inputSearchRef && inputSearchRef.current) {
                inputSearchRef.current.focus();
              }
            }, 100);
          }
        }
      },
      className: classNames(`${prefixClassName}-option`, {
        "is-open": isOpen,
        "has-error": error,
        "read-only": readOnly,
        disabled: disabled || isLoading
      }),
      ref: selectRef,
      // prevents downshift from auto adding prop
      "aria-labelledby": null
    }), /*#__PURE__*/React.createElement("div", {
      className: `${prefixClassName}-loading-wrapper`
    }, /*#__PURE__*/React.createElement("span", {
      className: `${prefixClassName}-content`
    }, isLoading && /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 100 100",
      xmlns: "http://www.w3.org/2000/svg",
      className: "loading-spinner"
    }, /*#__PURE__*/React.createElement("circle", {
      cx: "50",
      cy: "50",
      r: "45",
      className: "spinner"
    })))), /*#__PURE__*/React.createElement("input", {
      readOnly: true,
      className: classNames(`${prefixClassName}-input`, {
        "clearable-space": clearable && !isEmpty(selectedItem)
      }),
      placeholder: placeholder,
      value: inputValue,
      id: labelId,
      ref: inputRef,
      disabled: disabled || isLoading
    }), /*#__PURE__*/React.createElement("div", {
      className: `${prefixClassName}-icon-wrapper`
    }, clearable && !isEmpty(selectedItem) && /*#__PURE__*/React.createElement("span", {
      role: "button",
      tabIndex: disabled || readOnly ? -1 : 0,
      className: "close-icon",
      onClick: onClose,
      onKeyPress: onClose,
      style: {
        display: readOnly ? "none" : "flex"
      }
    }, /*#__PURE__*/React.createElement(Close, null)), /*#__PURE__*/React.createElement("span", {
      role: "button",
      tabIndex: disabled || readOnly ? -1 : 0,
      className: "expand-icon",
      onClick: onExpand,
      onKeyPress: onExpand,
      style: {
        display: readOnly ? "none" : "flex"
      }
    }, /*#__PURE__*/React.createElement(ExpandMore, null)))), /*#__PURE__*/React.createElement("div", {
      className: `${prefixClassName}-options-wrapper`,
      "aria-hidden": isOpen ? null : "true",
      ref: optionsRef,
      style: {
        position: "fixed",
        display: isOpen ? null : "none",
        zIndex: 11
      }
    }, isOpen && /*#__PURE__*/React.createElement(React.Fragment, null, shouldFilter || onInputChange ? /*#__PURE__*/React.createElement(Input, {
      ref: inputSearchRef,
      fluid: true,
      icon: true,
      placeholder: inputPlaceholder,
      onChange: handleSearchInputChange,
      value: searchText
    }, /*#__PURE__*/React.createElement("input", {
      style: {
        paddingLeft: TEXT_FIELD_OFFSET_WIDTH
      }
    }), /*#__PURE__*/React.createElement("i", {
      className: "icon svg-wrapper",
      style: {
        left: 0
      }
    }, /*#__PURE__*/React.createElement(Search, null)), searchText && /*#__PURE__*/React.createElement("i", {
      role: "button",
      tabIndex: "0",
      className: "icon link svg-wrapper",
      onKeyPress: onEnter(onClearInput),
      onClick: onClearInput
    }, /*#__PURE__*/React.createElement(Close, {
      className: "close-icon"
    }))) : /*#__PURE__*/React.createElement(React.Fragment, null), /*#__PURE__*/React.createElement("ul", _extends({}, getMenuProps(), {
      className: classNames(`${prefixClassName}-options`, {
        "has-error": error
      }),
      style: {
        maxHeight
      }
    }), filterOptions.length > 0 ? filterOptions.map((item, index) => /*#__PURE__*/React.createElement("li", getItemProps({
      key: item.label.trim() ? `${item.label}-${item.value}` : emptyCase,
      index,
      item,
      className: classNames(`${prefixClassName}-options-item`, {
        selected: item.value === selectedItem.value
      })
    }), item.label.trim() ? item.label : emptyCase)) : /*#__PURE__*/React.createElement("li", {
      tabIndex: "-1",
      onFocus: () => {
        inputRef.current.focus();
      },
      className: `${prefixClassName}-options-item no-results`
    }, "No results found.")))));
  }), error && !isEmpty(error) ? /*#__PURE__*/React.createElement("div", {
    className: "tui-invalid-feedback",
    children: error
  }) : "");
};
Select.displayName = "Select";
Select.propTypes = {
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  clearable: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.any.isRequired,
    label: PropTypes.string.isRequired
  })),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
  maxHeight: PropTypes.number,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  prefixClassName: PropTypes.string,
  id: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  required: PropTypes.bool,
  emptyCase: PropTypes.string,
  getRelativeViewportOffset: PropTypes.func,
  onInputChange: PropTypes.func,
  onBlur: PropTypes.func,
  shouldFilter: PropTypes.bool,
  inputPlaceholder: PropTypes.string
};
Select.defaultProps = {
  disabled: false,
  readOnly: false,
  clearable: false,
  error: undefined,
  options: [],
  value: "",
  style: {},
  maxHeight: 200,
  label: "",
  placeholder: "Select...",
  prefixClassName: "tui-select",
  id: undefined,
  onChange: noop,
  className: "",
  isLoading: false,
  required: false,
  emptyCase: "",
  shouldFilter: false
};
export default Select;
//# sourceMappingURL=Select.js.map